/**
 * Platform-wide API type definitions.
 *
 * These types mirror the backend's error contract (ADR-007) and
 * common response structures. All types here must stay in sync
 * with the backend's response records.
 */

/** Matches backend ErrorResponse record */
export interface ApiErrorResponse {
  code: string;
  message: string;
  timestamp: string;
  path: string;
  traceId: string;
  status?: number;
  error?: string;
  errors?: FieldError[];
}

/** Individual field-level validation error */
export interface FieldError {
  field: string;
  message: string;
}

/**
 * Typed API error thrown by the axios interceptor.
 * All components receive ApiError, never raw AxiosError.
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly fieldErrors?: FieldError[];

  constructor(response: ApiErrorResponse, status: number) {
    super(response.message);
    this.name = 'ApiError';
    this.code = response.code;
    this.status = status;
    this.fieldErrors = response.errors;
  }

  /** True if this is a validation error (400) */
  get isValidationError(): boolean {
    return this.code === 'VALIDATION_FAILED';
  }

  /** True if the user is not authenticated (401) */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** True if the user lacks permissions (403) */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True if the resource was not found (404) */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** Standard paginated response wrapper for future list endpoints */
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  first: boolean;
  last: boolean;
}
